import { useEffect, useState } from 'react';
import { api } from './api/client';

// GIS type declarations
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export default function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);

  // Fetch auth config from backend
  useEffect(() => {
    api.getAuthConfig().then((config) => {
      setGoogleClientId(config.googleClientId || '');
    });
  }, []);

  // Load GIS and trigger One Tap prompt (top-right overlay only)
  useEffect(() => {
    if (!googleClientId) return;

    // Google calls this with the One Tap credential. It lives inside the effect
    // because the effect is its only user, and it touches only state setters
    // (stable across renders) and the module-level api client.
    async function handleGoogleCredential(response: { credential: string }) {
      setError(null);
      setLoading(true);
      try {
        await api.googleOneTapLogin(response.credential);
        window.location.href = '/dashboard';
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Google sign-in failed');
        setLoading(false);
      }
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (!window.google) return;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleCredential,
        auto_select: true,
        cancel_on_tap_outside: false,
      });

      window.google.accounts.id.prompt();
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [googleClientId]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* Subtle grid background */}
      <div
        className="fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo and header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/20 mb-6">
            <svg className="w-10 h-10 text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Network Weather</h1>
          <p className="text-slate-500 mt-2">MSP Partner Portal</p>
        </div>

        {/* Login card */}
        <div className="bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white text-center mb-2">
            Sign in to your account
          </h2>
          <p className="text-slate-500 text-sm text-center mb-8">
            Use your corporate account to continue
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          {loading && (
            <div className="mb-4 text-slate-400 text-sm text-center">
              Signing in...
            </div>
          )}

          <div className="space-y-3">
            {/* Google Sign In — redirect fallback, One Tap handles the fast path */}
            <a
              href={api.getGoogleLoginUrl('/dashboard')}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 text-gray-900 font-medium rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </a>

            {/* Microsoft Sign In */}
            <a
              href={api.getMicrosoftLoginUrl('/dashboard')}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#2F2F2F] hover:bg-[#3F3F3F] text-white font-medium rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Continue with Microsoft
            </a>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800">
            <p className="text-slate-500 text-xs text-center">
              By signing in, you agree to our{' '}
              <a href="#" className="text-amber-400 hover:text-amber-300">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-amber-400 hover:text-amber-300">Privacy Policy</a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-slate-600 text-sm text-center mt-8">
          Need help?{' '}
          <a href="mailto:support@networkweather.com" className="text-amber-400 hover:text-amber-300">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
